/**
 * Tournament Configuration — UM report ownership (§A5, ruling R2′).
 *
 * Authority: `05-reporting-marking.md` §A5 (the responsibility matrix — *the owning UM drafts
 * and owns the report*), `07-configuration.md` Section 04 (`App Access`, the six managers)
 * and `apps/api/src/routes/configuration.ts`.
 *
 * **What this surface is.** The one place ownership is decided. Every other surface in the
 * system READS it — the Report Studio opens or refuses on it, `not_your_umpire` is decided by
 * it, the Reporting page's `Owned By UM` marker names it — and until this panel existed the
 * fixture bundle derived it round-robin and Postgres could only be seeded by hand. A derived
 * assignment cannot be handed over when a manager falls ill in week one.
 *
 * Four rules it keeps, three of them borrowed from `ConfigurationPanel` because ownership is
 * a configuration write like any other:
 *
 *  1. **Nothing is optimistic.** The row is disabled while the write is in flight and
 *     re-rendered from the server's own reply. A control that showed a hand-over the server
 *     had refused would send a manager to a Report Studio that will not open for her.
 *  2. **The whole surface is replaced on every write**, so the manager tallies cannot drift
 *     from the rows beneath them.
 *  3. **A refusal is stated where it happened** — the API's own sentence, on that umpire's
 *     row, with nothing paraphrased and nothing retried.
 *  4. **The panel says what a reassignment does and does not do**, in the foot, because that
 *     is the one thing a TD cannot see from the control: work already authored stays with its
 *     author, and only future work follows the new owner.
 */

import { useState } from "react";

import { ApiError, putOwnership } from "../../api/client.js";
import type { OwnershipAssignmentView, OwnershipBody } from "../../api/types.js";
import { tournamentDateLong } from "../../app/tournamentTime.js";

/**
 * The write, behind one seam. The real implementation is the API client's; the indirection
 * exists so a component test can drive the panel without a network.
 */
export interface OwnershipWriter {
  assign: (umpireTmsPersonId: string, ownerTmsPersonId: string) => Promise<OwnershipBody>;
}

export const LIVE_OWNERSHIP_WRITER: OwnershipWriter = { assign: putOwnership };

export interface OwnershipPanelProps {
  /** The surface as fetched. Held as seed state — there is one ownership map per tournament. */
  initialView: OwnershipBody;
  writer?: OwnershipWriter;
  /** Called after any write the server accepted, so the change log can refetch. */
  onWritten?: () => void;
}

interface RowError {
  umpireTmsPersonId: string;
  message: string;
}

export function OwnershipPanel({
  initialView,
  writer = LIVE_OWNERSHIP_WRITER,
  onWritten,
}: OwnershipPanelProps): React.JSX.Element {
  const [view, setView] = useState<OwnershipBody>(initialView);
  /** Exactly one row may be in flight, and it is named so the others can say why. */
  const [pending, setPending] = useState<string | null>(null);
  const [rowError, setRowError] = useState<RowError | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const busy = pending !== null;

  async function commit(row: OwnershipAssignmentView, ownerTmsPersonId: string): Promise<void> {
    // Choosing the manager who already owns her is not a write. The API would accept it and
    // append nothing; not sending it at all keeps the panel from reporting a hand-over that
    // did not happen.
    if (ownerTmsPersonId === row.ownerTmsPersonId) return;

    setPending(row.umpireTmsPersonId);
    setRowError(null);
    setNote(null);
    try {
      const next = await writer.assign(row.umpireTmsPersonId, ownerTmsPersonId);
      setView(next);
      const owner = next.managers.find((manager) => manager.tmsPersonId === ownerTmsPersonId);
      setNote(
        `${row.umpireMediaName} is now ${owner?.appShortName ?? "that manager"}'s. ` +
          `Marks, notes and the report already written are unchanged.`,
      );
      onWritten?.();
    } catch (error) {
      setNote(null);
      setRowError({
        umpireTmsPersonId: row.umpireTmsPersonId,
        message:
          error instanceof ApiError
            ? error.message
            : "The assignment was not saved. Ownership is unchanged on the server.",
      });
    } finally {
      setPending(null);
    }
  }

  return (
    <section className="panel panel--d1" aria-labelledby="cfgOwnTitle" style={{ marginTop: 22 }} data-howto="report-ownership">
      <div className="panel__head">
        <h3 className="panel__title" id="cfgOwnTitle">
          Umpire Report Ownership
        </h3>
        <span className="panel__note">One Umpires Manager Per Umpire · Stays Editable</span>
        <span className="spacer" />
        <span
          className={`statuspill ${
            view.unassignedCount === 0 ? "statuspill--good" : "statuspill--warn"
          }`}
          data-testid="ownership-unassigned"
        >
          <span className="sdot" aria-hidden="true" />
          {view.unassignedCount === 0
            ? `All ${String(view.assignments.length)} umpires assigned`
            : `${String(view.unassignedCount)} of ${String(view.assignments.length)} unassigned`}
        </span>
      </div>

      {note === null ? null : (
        <p className="apptnote" role="status" data-testid="ownership-note">
          {note}
        </p>
      )}

      {view.managers.length === 0 ? (
        <div className="emptystate">
          <b>No Umpires Managers Yet</b>
          Ownership is assigned to a manager with an app account, and this tournament has none
          set up. Nobody has been left unassigned by mistake — there is nobody to assign to
          yet.
        </div>
      ) : (
        <p className="critnote" data-testid="ownership-tally">
          {view.managers
            .map((manager) => `${manager.appShortName} ${String(manager.ownedCount)}`)
            .join(" · ")}
        </p>
      )}

      {view.assignments.length === 0 ? (
        <div className="emptystate">
          <b>No Umpires On The Roster</b>
          Only umpires are owned by an Umpires Manager. Every other report — ATD, Technical
          Officer, Judge and Umpires Manager — is the Technical Delegate&rsquo;s solely.
        </div>
      ) : (
        <table className="mtable" aria-label="Umpire report ownership">
          <thead>
            <tr>
              <th scope="col">Umpire</th>
              <th scope="col">Country</th>
              <th scope="col">Owning Umpires Manager</th>
              <th scope="col">Assigned</th>
            </tr>
          </thead>
          <tbody>
            {view.assignments.map((row) => (
              <OwnershipRow
                key={row.umpireTmsPersonId}
                row={row}
                managers={view.managers}
                busy={busy}
                saving={pending === row.umpireTmsPersonId}
                error={
                  rowError?.umpireTmsPersonId === row.umpireTmsPersonId ? rowError.message : null
                }
                onAssign={(owner) => void commit(row, owner)}
              />
            ))}
          </tbody>
        </table>
      )}

      <div className="panel__foot">
        <b>Changing an owner changes who writes next, and nothing else.</b> Notes keep their
        author, marks and the report keep the name of whoever entered them, and a report
        already pushed keeps the manager who pushed it on its receipt. The incoming manager
        inherits the draft as it stands — there is one report per umpire for the whole
        tournament — and can open the Report Studio for that umpire immediately; the outgoing
        manager can no longer write about her, and her own unreleased notes stay hers alone.
        <br />
        Ownership moves from one manager to another. It cannot be taken away, because an
        umpire nobody owns is an umpire nobody can report on.
      </div>
    </section>
  );
}

/* ── One umpire ───────────────────────────────────────────────────────────── */

interface OwnershipRowProps {
  row: OwnershipAssignmentView;
  managers: OwnershipBody["managers"];
  busy: boolean;
  saving: boolean;
  error: string | null;
  onAssign: (ownerTmsPersonId: string) => void;
}

/**
 * `assigned_at` as a date the TD reads at a glance. The instant is stored in UTC and rendered
 * in the tournament's own zone by the rest of the suite; here the DATE is the whole of the
 * useful fact — "when did this last move" — so the time is not printed and cannot be mistaken
 * for a match-clock stamp.
 *
 * **RUN 83 — through the shared reader.** This was the one surface in the sweep already naming
 * the zone, and it named it as a bare `"Asia/Tokyo"` of its own. The answer was right and the
 * arrangement was the thing the sweep was undoing: eleven surfaces each deciding separately what
 * the tournament's clock is, which is how ten of them came to decide it was the reader's laptop.
 */
function assignedOn(assignedAt: string | null): string {
  if (assignedAt === null) return "—";
  const parsed = new Date(assignedAt);
  if (Number.isNaN(parsed.getTime())) return "—";
  return tournamentDateLong(parsed);
}

function OwnershipRow({
  row,
  managers,
  busy,
  saving,
  error,
  onAssign,
}: OwnershipRowProps): React.JSX.Element {
  const unassigned = row.ownerTmsPersonId === null;

  return (
    <tr
      data-testid={`ownership-${row.umpireTmsPersonId}`}
      className={saving ? "is-saving" : undefined}
    >
      <th scope="row">{row.umpireMediaName}</th>
      <td className="num">{row.umpireCountry}</td>

      <td>
        <span className="slotpick">
          <select
            // The label names the umpire. Twenty controls called "Owner" are twenty controls
            // a screen reader cannot tell apart — and nor can a test.
            aria-label={`Owning Umpires Manager for ${row.umpireMediaName}`}
            data-testid={`ownership-select-${row.umpireTmsPersonId}`}
            value={row.ownerTmsPersonId ?? ""}
            disabled={busy || managers.length === 0}
            onChange={(event) => {
              onAssign(event.target.value);
            }}
          >
            {/*
              The unassigned state is offered as a value only while it IS the state: it is
              not a choice — ownership cannot be taken away — and an option that produced a
              refusal every time it was picked would be a control that lies about its own
              range.
            */}
            {unassigned ? <option value="">Not yet assigned</option> : null}
            {managers.map((manager) => (
              <option key={manager.tmsPersonId} value={manager.tmsPersonId}>
                {manager.appShortName} · {manager.mediaName}
              </option>
            ))}
          </select>
        </span>
        {saving ? (
          <span className="slotlock" data-testid={`ownership-saving-${row.umpireTmsPersonId}`}>
            Saving…
          </span>
        ) : null}
        {error === null ? null : (
          <span
            className="rowerror"
            role="alert"
            data-testid={`ownership-error-${row.umpireTmsPersonId}`}
          >
            {error}
          </span>
        )}
      </td>

      <td>
        {unassigned ? (
          <span className="btnhint" data-testid={`ownership-never-${row.umpireTmsPersonId}`}>
            Never assigned
          </span>
        ) : (
          <span className="num">{assignedOn(row.assignedAt)}</span>
        )}
      </td>
    </tr>
  );
}
